import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import './Dashboard.css';

const Dashboard = ({ theme, onToggleTheme }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ q: '', state: '', dateFrom: '', dateTo: '' });
  const [user, setUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem('ismo.dashboard.page_size');
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return [20, 50, 100, 200].includes(parsed) ? parsed : 20;
  });
  const [totalRequests, setTotalRequests] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const raw = localStorage.getItem('ismo.dashboard.visible_columns');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {}
    }
    return ['id', 'client_name', 'client_email', 'client_phone', 'state', 'monto', 'fecha_solicitud', 'agencia'];
  });
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [permissions, setPermissions] = useState({
    is_fad_user: false,
    is_fad_readonly: false,
    is_fad_supervisor: false,
    is_fad_admin: false,
    can_create_request_fad: false,
    can_upload_documents_fad: false
  });
  const [settingPaymented, setSettingPaymented] = useState({});
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [paymentConfirmRequestId, setPaymentConfirmRequestId] = useState(null);
  const [paymentConfirmForm, setPaymentConfirmForm] = useState({
    fecha_prog_pago: '',
    payment_details: '',
  });
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [confirmPaymentError, setConfirmPaymentError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingSolicitud, setCreatingSolicitud] = useState(false);
  const [createError, setCreateError] = useState('');
  const [exportingRequests, setExportingRequests] = useState(false);
  const [createForm, setCreateForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    agencia: '',
    cliente_id: '',
    monto: '',
    fecha_solicitud: new Date().toISOString().slice(0, 10),
  });
  const [activePage, setActivePage] = useState('dashboard');
  const [settingsSection, setSettingsSection] = useState('users');
  const [portalUsers, setPortalUsers] = useState([]);
  const [portalUsersLoading, setPortalUsersLoading] = useState(false);
  const [portalUsersError, setPortalUsersError] = useState('');
  const [portalUserEdits, setPortalUserEdits] = useState({});
  const [savingPortalUserId, setSavingPortalUserId] = useState(null);
  const [changingPasswordUserId, setChangingPasswordUserId] = useState(null);
  const [deletingPortalUserId, setDeletingPortalUserId] = useState(null);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [creatingPortalUser, setCreatingPortalUser] = useState(false);
  const [createPortalUserError, setCreatePortalUserError] = useState('');
  const [createPortalUserForm, setCreatePortalUserForm] = useState({
    name: '',
    login: '',
    password: '',
    role: '',
    is_fad_admin: false,
    can_create_request_fad: false,
    can_upload_documents_fad: false,
    can_validate_documents: false,
    can_cancel_requests: false,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const userInfo = ApiService.getUserInfo();
    if (!userInfo) {
      navigate('/login');
      return;
    }
    setUser(userInfo);
    loadPermissions();
  }, [navigate]);

  useEffect(() => {
    localStorage.setItem('ismo.dashboard.page_size', String(pageSize));
  }, [pageSize]);

  useEffect(() => {
    localStorage.setItem('ismo.dashboard.visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((totalRequests || 0) / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [totalRequests, pageSize]);

  useEffect(() => {
    if (activePage !== 'dashboard') return;
    loadRequests({ page, pageSize, ...appliedFilters });
  }, [activePage, page, pageSize, appliedFilters]);

  const loadRequests = async ({ page, pageSize, q, state, dateFrom, dateTo } = {}) => {
    try {
      setLoading(true);
      const payload = await ApiService.getRefundRequests({
        page,
        pageSize,
        q,
        state,
        dateFrom,
        dateTo,
      });
      setRequests(payload?.results || []);
      setTotalRequests(payload?.total || 0);
    } catch (error) {
      setError('Error al cargar las solicitudes');
      console.error('Error:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await ApiService.getUserPermissions();
      if (response.status === 'success') {
        setPermissions(response.result);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Si hay error, mantener permisos por defecto (sin permisos)
    }
  };

  const handleSetPaymented = async (requestId) => {
    try {
      setSettingPaymented(prev => ({ ...prev, [requestId]: true }));
      
      await ApiService.setStatePaymented(requestId);
      
      // Recargar las solicitudes para reflejar el cambio
      await loadRequests({ page, pageSize, ...appliedFilters });
    } catch (error) {
      console.error('Error setting paymented state:', error);
      setError('Error al actualizar el estado de la solicitud');
    } finally {
      setSettingPaymented(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleOpenPaymentConfirmModal = (requestId) => {
    const targetRequest = requests.find(r => r?.id === requestId);
    const defaultFecha = targetRequest?.fecha_prog_pago ? String(targetRequest.fecha_prog_pago).slice(0, 10) : '';
    setPaymentConfirmRequestId(requestId);
    setPaymentConfirmForm({
      fecha_prog_pago: defaultFecha,
      payment_details: '',
    });
    setConfirmPaymentError('');
    setShowPaymentConfirmModal(true);
  };

  const handlePaymentConfirmInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentConfirmForm(prev => ({ ...prev, [name]: value }));
  };

  const handleConfirmPaymented = async () => {
    if (!paymentConfirmRequestId) return;
    if (!paymentConfirmForm.fecha_prog_pago.trim()) {
      setConfirmPaymentError('Debes capturar la fecha programada de pago para continuar.');
      return;
    }
    try {
      setConfirmingPayment(true);
      setConfirmPaymentError('');
      setSettingPaymented(prev => ({ ...prev, [paymentConfirmRequestId]: true }));
      await ApiService.setStatePaymented(paymentConfirmRequestId, {
        fecha_prog_pago: paymentConfirmForm.fecha_prog_pago,
        payment_details: paymentConfirmForm.payment_details,
      });
      setShowPaymentConfirmModal(false);
      setPaymentConfirmRequestId(null);
      await loadRequests({ page, pageSize, ...appliedFilters });
    } catch (error) {
      const message = error?.response?.data?.error || 'Error al actualizar el estado de la solicitud';
      setConfirmPaymentError(message);
      console.error('Error setting paymented state:', error);
    } finally {
      setConfirmingPayment(false);
      if (paymentConfirmRequestId) {
        setSettingPaymented(prev => ({ ...prev, [paymentConfirmRequestId]: false }));
      }
    }
  };

  const handleLogout = async () => {
    await ApiService.logout();
    navigate('/login');
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest('.user-menu')) {
      setShowUserMenu(false);
    }
    if (!event.target.closest('.columns-menu')) {
      setShowColumnsMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleRequestClick = (requestId) => {
    if (permissions.is_fad_admin) return;
    navigate(`/request/${requestId}`);
  };

  const handleOpenCreateModal = () => {
    setCreateError('');
    setCreateForm({
      client_name: '',
      client_email: '',
      client_phone: '',
      agencia: '',
      cliente_id: '',
      monto: '',
      fecha_solicitud: new Date().toISOString().slice(0, 10),
    });
    setShowCreateModal(true);
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setCreateForm(prev => ({ ...prev, [name]: value }));
    if (createError) setCreateError('');
  };

  const handleCreateSolicitud = async () => {
    try {
      setCreatingSolicitud(true);
      setCreateError('');
      const response = await ApiService.createSolicitud(createForm);
      console.log('Response:', response);
      if (response?.result?.status === 'success' && response?.result?.id) {
        setShowCreateModal(false);
        await loadRequests({ page, pageSize, ...appliedFilters });
        navigate(`/request/${response.result.id}`);
        return;
      }
      setCreateError('No se pudo crear la solicitud');
    } catch (error) {
      setCreateError('Error al crear la solicitud');
      console.error('Error:', error);
    } finally {
      setCreatingSolicitud(false);
    }
  };

  const loadPortalUsers = async () => {
    try {
      setPortalUsersLoading(true);
      setPortalUsersError('');
      const response = await ApiService.adminGetUsers();
      if (response?.status === 'success') {
        const list = response.result || [];
        setPortalUsers(list);
        const nextEdits = {};
        for (const u of list) {
          nextEdits[u.id] = {
            role: u.role || '',
            is_fad_admin: !!u.is_fad_admin,
            can_create_request_fad: !!u.can_create_request_fad,
            can_upload_documents_fad: !!u.can_upload_documents_fad,
            can_validate_documents: !!u.can_validate_documents,
            can_cancel_requests: !!u.can_cancel_requests,
          };
        }
        setPortalUserEdits(nextEdits);
        return;
      }
      setPortalUsers([]);
      setPortalUserEdits({});
      setPortalUsersError('No se pudieron cargar los usuarios');
    } catch (error) {
      console.error('Error cargando usuarios del portal:', error);
      setPortalUsers([]);
      setPortalUserEdits({});
      setPortalUsersError('Error al cargar los usuarios');
    } finally {
      setPortalUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!permissions.is_fad_admin) return;
    if (activePage !== 'settings') return;
    loadPortalUsers();
  }, [activePage, permissions.is_fad_admin]);

  const handlePortalUserEditChange = (userId, field, value) => {
    setPortalUserEdits(prev => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
  };

  const handleSavePortalUser = async (userId) => {
    const payload = portalUserEdits[userId] || {};
    try {
      setSavingPortalUserId(userId);
      setPortalUsersError('');
      await ApiService.adminUpdateUser(userId, payload);
      await loadPortalUsers();
    } catch (error) {
      console.error('Error actualizando usuario del portal:', error);
      setPortalUsersError('Error al guardar cambios del usuario');
    } finally {
      setSavingPortalUserId(null);
    }
  };

  const handleChangePortalUserPassword = async (portalUser) => {
    if (!portalUser?.id) return;

    const newPassword = window.prompt(`Nueva contraseña para "${portalUser.name}"`, '');
    if (newPassword === null) return;
    if (!newPassword.trim()) {
      setPortalUsersError('La contraseña no puede estar vacía');
      return;
    }

    try {
      setChangingPasswordUserId(portalUser.id);
      setPortalUsersError('');
      await ApiService.adminChangeUserPassword(portalUser.id, newPassword.trim());
    } catch (error) {
      console.error('Error cambiando contraseña de usuario:', error);
      setPortalUsersError('Error al cambiar la contraseña del usuario');
    } finally {
      setChangingPasswordUserId(null);
    }
  };

  const handleDeletePortalUser = async (portalUser) => {
    if (!portalUser?.id) return;
    if (portalUser.id === user?.uid) {
      setPortalUsersError('No puede eliminar su propio usuario');
      return;
    }
    if (!window.confirm(`¿Eliminar del portal al usuario "${portalUser.name}"?`)) return;

    try {
      setDeletingPortalUserId(portalUser.id);
      setPortalUsersError('');
      await ApiService.adminDeleteUser(portalUser.id);
      await loadPortalUsers();
    } catch (error) {
      console.error('Error eliminando usuario del portal:', error);
      setPortalUsersError('Error al eliminar usuario del portal');
    } finally {
      setDeletingPortalUserId(null);
    }
  };

  const handleOpenCreateUserModal = () => {
    setCreatePortalUserError('');
    setCreatePortalUserForm({
      name: '',
      login: '',
      password: '',
      role: '',
      is_fad_admin: false,
      can_create_request_fad: false,
      can_upload_documents_fad: false,
      can_validate_documents: false,
      can_cancel_requests: false,
    });
    setShowCreateUserModal(true);
  };

  const handleCreatePortalUserInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreatePortalUserForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (createPortalUserError) setCreatePortalUserError('');
  };

  const handleCreatePortalUser = async () => {
    try {
      setCreatingPortalUser(true);
      setCreatePortalUserError('');
      const response = await ApiService.adminCreateUser(createPortalUserForm);
      if (response?.result?.status === 'success' && response?.result?.id) {
        setShowCreateUserModal(false);
        await loadPortalUsers();
        return;
      }
      setCreatePortalUserError('No se pudo crear el usuario');
    } catch (error) {
      console.error('Error creando usuario del portal:', error);
      setCreatePortalUserError('Error al crear el usuario');
    } finally {
      setCreatingPortalUser(false);
    }
  };



  const getStateColor = (state) => {
    switch (state) {
      case 'Draft':
        return '#6c757d';
      case 'Sent':
        return '#3884D7';
      case 'Pending':
        return '#ffc107';
      case 'Signed':
        return '#28a745';
      case 'Expired':
        return '#dc3545';
      case 'Cancelled':
        return '#D7091C';
      case 'Rejected':
      case 'Rejected_User':
        return '#dc3545';
      case 'Paymented':
        return '#0ED4B7';
      case 'onValidation':
        return '#a7441aff';
      default:
        return '#6c757d';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const totalPages = Math.max(1, Math.ceil((totalRequests || 0) / pageSize));
  const showActionsColumn = permissions.is_fad_user || permissions.is_fad_readonly;

  const dataColumns = [
    { key: 'id', label: '#', render: (r) => r.id ?? '-' },
    { key: 'client_name', label: 'Nombre del Cliente', render: (r) => r.client_name || '-' },
    { key: 'client_email', label: 'Correo Electrónico', render: (r) => r.client_email || '-' },
    { key: 'client_phone', label: 'Teléfono', render: (r) => r.client_phone || '-' },
    { key: 'agencia', label: 'Agencia', render: (r) => r.agencia || '-' },
    { key: 'requisitionId', label: 'Requisition ID', render: (r) => r.requisitionId || '-' },
    { key: 'tipo_pago', label: 'Tipo de Pago', render: (r) => r.tipo_pago || '-' },
    { key: 'state', label: 'Estado De la Solicitud', render: (r) => ApiService.getStateTranslation(r.state) },
    { key: 'monto', label: 'Monto', render: (r) => formatCurrency(r.monto) },
    { key: 'fecha_solicitud', label: 'Fecha de Solicitud', render: (r) => formatDate(r.fecha_solicitud) },
  ];

  const visibleDataColumns = dataColumns.filter((c) => visibleColumns.includes(c.key));
  const toggleColumnVisibility = (key) => {
    setVisibleColumns((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const handleChangePageSize = (e) => {
    const next = parseInt(e.target.value, 10);
    if (![20, 50, 100, 200].includes(next)) return;
    setPageSize(next);
    setPage(1);
  };

  const handleApplyFilters = () => {
    setPage(1);
    setAppliedFilters({
      q: (searchTerm || '').trim(),
      state: filterState || '',
      dateFrom: filterDateFrom || '',
      dateTo: filterDateTo || '',
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterState('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
    setAppliedFilters({ q: '', state: '', dateFrom: '', dateTo: '' });
  };

  const escapeExcelValue = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };

  const handleExportRequests = async () => {
    try {
      setExportingRequests(true);

      const allRequests = await ApiService.getRefundRequestsAll({
        q: appliedFilters.q,
        state: appliedFilters.state,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo,
      });
      const headers = dataColumns.map((c) => c.label);
      const rows = allRequests.map((request) => dataColumns.map((c) => c.key === 'state'
        ? ApiService.getStateTranslation(request.state)
        : c.render(request)
      ));

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="utf-8" />
          </head>
          <body>
            <table>
              <thead>
                <tr>${headers.map((header) => `<th>${escapeExcelValue(header)}</th>`).join('')}</tr>
              </thead>
              <tbody>
                ${rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeExcelValue(cell)}</td>`).join('')}</tr>`).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], {
        type: 'application/vnd.ms-excel;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `solicitudes_${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exportando solicitudes:', error);
      setError('Error al exportar las solicitudes');
    } finally {
      setExportingRequests(false);
    }
  };

  return (
    <div className="dashboard-container">
      <header className="header">
        <div className="header-left">
          <div className="logo">
            COSIT - Devoluciones
          </div>
          <nav className="nav">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setActivePage('dashboard'); }}
              className={activePage === 'dashboard' ? 'active' : ''}
            >
              Dashboard
            </a>
            {permissions.is_fad_admin && (
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); setActivePage('settings'); }}
                className={activePage === 'settings' ? 'active' : ''}
              >
                Configuración
              </a>
            )}
            {/* <a href="#">Requests</a>
            <a href="#">Clients</a>
            <a href="#">Reports</a> */}
          </nav>
        </div>

        <div className="header-right">
          <button
            type="button"
            className="theme-toggle"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
            title={theme === 'dark' ? 'Tema claro' : 'Tema oscuro'}
          >
            {theme === 'dark' ? '☀' : '🌙'}
          </button>
          <div className="user-menu">
            <div className="user-profile" onClick={toggleUserMenu}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-info">
                  <span className="user-name">{user?.name || 'Usuario'}</span>
                </div>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout-btn" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content" style={{ maxWidth: 'max-content' }}>
        {activePage === 'dashboard' ? (
          <>
            <div className="page-header">
              <div>
                <h1>Solicitudes de Devolución</h1>
                <p>Administre y rastree todas las solicitudes de devolución</p>
              </div>
              <div className="page-header-actions">
                <div className="columns-menu">
                  <button
                    type="button"
                    onClick={() => setShowColumnsMenu((v) => !v)}
                    className="btn btn-secondary"
                  >
                    Columnas
                  </button>
                  {showColumnsMenu && (
                    <div className="columns-dropdown">
                      {dataColumns.map((col) => (
                        <label key={col.key} className="columns-option">
                          <input
                            type="checkbox"
                            checked={visibleColumns.includes(col.key)}
                            onChange={() => toggleColumnVisibility(col.key)}
                          />
                          <span>{col.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleExportRequests}
                  className="btn btn-success"
                  disabled={exportingRequests || totalRequests === 0}
                >
                  {exportingRequests ? 'Exportando...' : 'Exportar Excel'}
                </button>
                {permissions.can_create_request_fad && !permissions.is_fad_admin && (
                  <button
                    type="button"
                    onClick={handleOpenCreateModal}
                    className="btn btn-primary"
                  >
                    Nueva solicitud
                  </button>
                )}
              </div>
            </div>
            <div className="help-accordion">
              <button 
                className="help-toggle"
                onClick={() => setShowHelp(!showHelp)}
              >
                <span>📋 Guía de Estados</span>
                <span className={`arrow ${showHelp ? 'open' : ''}`}>▼</span>
              </button>
              
              {showHelp && (
                <div className="help-content">
                  <div className="help-grid">
                    {[
                      'Draft', 'Sent', 'Pending', 'Signed', 
                      'Paymented', 'Expired', 'Cancelled', 'Rejected', 'Rejected_User', 'onValidation'
                    ].map(state => (
                      <div key={state} className="help-item">
                        <div className="help-state">
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStateColor(state) }}
                          >
                            {ApiService.getStateTranslation(state)}
                          </span>
                        </div>
                        <div className="help-description">
                          {ApiService.getStateDescription(state)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="error">
                {error}
                <button onClick={() => loadRequests({ page, pageSize, ...appliedFilters })} className="btn btn-secondary">
                  Reintentar
                </button>
              </div>
            )}

            <div className="filters-bar">
              <div className="filters-row">
                <div className="filters-field" style={{ flex: 1 }}>
                  <label>Buscar</label>
                  <input
                    type="text"
                    placeholder="ID, cliente, correo, teléfono, recibo, agencia, requisition..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleApplyFilters();
                    }}
                    className="form-control"
                  />
                </div>

                <div className="filters-field">
                  <label>Estado</label>
                  <select
                    value={filterState}
                    onChange={(e) => setFilterState(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Todos</option>
                    {[
                      'Draft', 'Sent', 'Pending', 'Signed',
                      'Paymented', 'Expired', 'Cancelled', 'Rejected', 'Rejected_User', 'onValidation'
                    ].map((s) => (
                      <option key={s} value={s}>
                        {ApiService.getStateTranslation(s)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filters-field">
                  <label>Fecha (desde)</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="form-control"
                  />
                </div>

                <div className="filters-field">
                  <label>Fecha (hasta)</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="form-control"
                  />
                </div>

                <div className="filters-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleApplyFilters}
                    disabled={loading}
                  >
                    Buscar
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleClearFilters}
                    disabled={loading && totalRequests === 0}
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              </div>
            </div>

            <div className="requests-table-container">
              <table className="table requests-table">
                <thead>
                  <tr>
                    {visibleDataColumns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                    {showActionsColumn && (
                      <th>Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={visibleDataColumns.length + (showActionsColumn ? 1 : 0)} className="no-data">
                        Cargando solicitudes...
                      </td>
                    </tr>
                  ) : requests.length === 0 ? (
                    <tr>
                      <td colSpan={visibleDataColumns.length + (showActionsColumn ? 1 : 0)} className="no-data">
                        {appliedFilters.q || appliedFilters.state || appliedFilters.dateFrom || appliedFilters.dateTo
                          ? 'No se encontraron solicitudes que coincidan con la búsqueda'
                          : 'No hay solicitudes disponibles'}
                      </td>
                    </tr>
                  ) : (
                    requests.map((request) => (
                      <tr
                        key={request.id}
                        onClick={() => handleRequestClick(request.id)}
                        className="request-row"
                        style={{ cursor: permissions.is_fad_admin ? 'default' : 'pointer' }}
                      >
                        {visibleDataColumns.map((col) => (
                          <td key={col.key}>
                            {col.key === 'state' ? (
                              <span
                                className="status-badge"
                                style={{ backgroundColor: getStateColor(request.state) }}
                              >
                                {ApiService.getStateTranslation(request.state)}
                              </span>
                            ) : (
                              col.render(request)
                            )}
                          </td>
                        ))}
                        {showActionsColumn && (
                          <td className="actions">
                            {request.state === 'Signed' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPaymentConfirmModal(request.id);
                                }}
                                className="btn btn-warning btn-sm"
                                disabled={settingPaymented[request.id] || confirmingPayment}
                              >
                                {settingPaymented[request.id] ? 'Enviando...' : 'Enviar notificación de pago'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-footer">
              <div className="results-count">
                Mostrando {requests.length} de {totalRequests} solicitudes
              </div>
              <div className="pagination-controls">
                <div className="pagination-size">
                  <span>Por página</span>
                  <select value={pageSize} onChange={handleChangePageSize} className="form-control">
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
                <div className="pagination-buttons">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    Anterior
                  </button>
                  <div className="pagination-page">
                    Página {page} de {totalPages}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="page-header">
              <div>
                <h1>Configuración</h1>
                <p>Administración de usuarios del portal</p>
              </div>
            </div>

            <div style={{ marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <select
                value={settingsSection}
                onChange={(e) => setSettingsSection(e.target.value)}
                className="form-control"
                style={{ maxWidth: '280px' }}
              >
                <option value="users">Usuarios</option>
              </select>
              <button
                type="button"
                onClick={handleOpenCreateUserModal}
                className="btn btn-primary"
              >
                Nuevo usuario
              </button>
              <button
                type="button"
                onClick={loadPortalUsers}
                className="btn btn-secondary"
                disabled={portalUsersLoading}
              >
                {portalUsersLoading ? 'Cargando...' : 'Recargar'}
              </button>
            </div>

            {portalUsersError && (
              <div className="error">
                {portalUsersError}
              </div>
            )}

            <div className="requests-table-container">
              <table className="table requests-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Usuario</th>
                    <th>Rol</th>
                    <th>Puede crear devoluciones</th>
                    <th>Puede cargar documentos</th>
                    <th>Validar documentos</th>
                    <th>Cancelar solicitudes</th>
                    <th>Usuario Admin</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {portalUsersLoading ? (
                    <tr>
                      <td colSpan="9" className="no-data">Cargando usuarios...</td>
                    </tr>
                  ) : portalUsers.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-data">No hay usuarios visibles en el portal</td>
                    </tr>
                  ) : (
                    portalUsers.map((u) => {
                      const edit = portalUserEdits[u.id] || {};
                      return (
                        <tr key={u.id}>
                          <td className="client-name">{u.name}</td>
                          <td className="client-email">{u.login}</td>
                          <td>
                            <select
                              className="form-control"
                              value={edit.role || ''}
                              onChange={(e) => handlePortalUserEditChange(u.id, 'role', e.target.value)}
                            >
                              <option value="">Ninguno</option>
                              <option value="fad_user">User</option>
                              <option value="fad_readonly">Read Only</option>
                              <option value="fad_supervisor">Supervisor</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!edit.can_create_request_fad}
                              onChange={(e) => handlePortalUserEditChange(u.id, 'can_create_request_fad', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!edit.can_upload_documents_fad}
                              onChange={(e) => handlePortalUserEditChange(u.id, 'can_upload_documents_fad', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!edit.can_validate_documents}
                              onChange={(e) => handlePortalUserEditChange(u.id, 'can_validate_documents', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!edit.can_cancel_requests}
                              onChange={(e) => handlePortalUserEditChange(u.id, 'can_cancel_requests', e.target.checked)}
                            />
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!edit.is_fad_admin}
                              onChange={(e) => handlePortalUserEditChange(u.id, 'is_fad_admin', e.target.checked)}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleSavePortalUser(u.id)}
                              disabled={savingPortalUserId === u.id}
                            >
                              {savingPortalUserId === u.id ? 'Guardando...' : 'Guardar'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => handleChangePortalUserPassword(u)}
                              disabled={changingPasswordUserId === u.id}
                              style={{ marginLeft: '8px' }}
                            >
                              {changingPasswordUserId === u.id ? 'Cambiando...' : 'Contraseña'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => handleDeletePortalUser(u)}
                              disabled={deletingPortalUserId === u.id || u.id === user?.uid}
                              style={{ marginLeft: '8px' }}
                            >
                              {deletingPortalUserId === u.id ? 'Eliminando...' : 'Eliminar'}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Crear solicitud</h3>
            {createError && (
              <div className="error">
                {createError}
              </div>
            )}
            <div className="modal-form">
              <div className="modal-row">
                <label>Nombre del Cliente *</label>
                <input
                  type="text"
                  name="client_name"
                  value={createForm.client_name}
                  onChange={handleCreateInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Correo Electrónico *</label>
                <input
                  type="email"
                  name="client_email"
                  value={createForm.client_email}
                  onChange={handleCreateInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="client_phone"
                  value={createForm.client_phone}
                  onChange={handleCreateInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Agencia</label>
                <input
                  type="text"
                  name="agencia"
                  value={createForm.agencia}
                  onChange={handleCreateInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Cliente ID</label>
                <input
                  type="text"
                  name="cliente_id"
                  value={createForm.cliente_id}
                  onChange={handleCreateInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Monto *</label>
                <input
                  type="number"
                  step="0.01"
                  name="monto"
                  value={createForm.monto}
                  onChange={handleCreateInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Fecha de Solicitud *</label>
                <input
                  type="date"
                  name="fecha_solicitud"
                  value={createForm.fecha_solicitud}
                  onChange={handleCreateInputChange}
                />
              </div>
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="btn btn-secondary"
                disabled={creatingSolicitud}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateSolicitud}
                className="btn btn-primary"
                disabled={creatingSolicitud || !createForm.client_name.trim() || !createForm.client_email.trim() || !String(createForm.monto).trim()}
              >
                {creatingSolicitud ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateUserModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <h3>Crear usuario</h3>
            {createPortalUserError && (
              <div className="error">
                {createPortalUserError}
              </div>
            )}
            <div className="modal-form">
              <div className="modal-row">
                <label>Nombre *</label>
                <input
                  type="text"
                  name="name"
                  value={createPortalUserForm.name}
                  onChange={handleCreatePortalUserInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Login (usuario o correo) *</label>
                <input
                  type="text"
                  name="login"
                  value={createPortalUserForm.login}
                  onChange={handleCreatePortalUserInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Contraseña *</label>
                <input
                  type="password"
                  name="password"
                  value={createPortalUserForm.password}
                  onChange={handleCreatePortalUserInputChange}
                />
              </div>
              <div className="modal-row">
                <label>Rol</label>
                <select
                  className="form-control"
                  name="role"
                  value={createPortalUserForm.role}
                  onChange={handleCreatePortalUserInputChange}
                >
                  <option value="">Ninguno</option>
                  <option value="fad_user">User</option>
                  <option value="fad_readonly">Read Only</option>
                  <option value="fad_supervisor">Supervisor</option>
                </select>
              </div>
              <div className="modal-row">
                <label>
                  <input
                    type="checkbox"
                    name="can_create_request_fad"
                    checked={createPortalUserForm.can_create_request_fad}
                    onChange={handleCreatePortalUserInputChange}
                  />{' '}
                  Puede crear devoluciones
                </label>
              </div>
              <div className="modal-row">
                <label>
                  <input
                    type="checkbox"
                    name="can_upload_documents_fad"
                    checked={createPortalUserForm.can_upload_documents_fad}
                    onChange={handleCreatePortalUserInputChange}
                  />{' '}
                  Puede cargar documentos
                </label>
              </div>
              <div className="modal-row">
                <label>
                  <input
                    type="checkbox"
                    name="can_validate_documents"
                    checked={createPortalUserForm.can_validate_documents}
                    onChange={handleCreatePortalUserInputChange}
                  />{' '}
                  Validar documentos
                </label>
              </div>
              <div className="modal-row">
                <label>
                  <input
                    type="checkbox"
                    name="can_cancel_requests"
                    checked={createPortalUserForm.can_cancel_requests}
                    onChange={handleCreatePortalUserInputChange}
                  />{' '}
                  Cancelar solicitudes
                </label>
              </div>
              <div className="modal-row">
                <label>
                  <input
                    type="checkbox"
                    name="is_fad_admin"
                    checked={createPortalUserForm.is_fad_admin}
                    onChange={handleCreatePortalUserInputChange}
                  />{' '}
                  Usuario Admin
                </label>
              </div>
            </div>
            <div className="modal-buttons">
              <button
                type="button"
                onClick={() => setShowCreateUserModal(false)}
                className="btn btn-secondary"
                disabled={creatingPortalUser}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreatePortalUser}
                className="btn btn-primary"
                disabled={creatingPortalUser || !createPortalUserForm.name.trim() || !createPortalUserForm.login.trim() || !createPortalUserForm.password}
              >
                {creatingPortalUser ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentConfirmModal && paymentConfirmRequestId && (
        <div className="modal-overlay" onClick={(e) => {
          if (!confirmingPayment && e.target.classList.contains('modal-overlay')) {
            setShowPaymentConfirmModal(false);
            setPaymentConfirmRequestId(null);
          }
        }}>
          <div className="modal-container" style={{ maxWidth: '560px' }}>
            {(() => {
              const targetRequest = requests.find(r => r?.id === paymentConfirmRequestId) || {};
              return (
                <>
                  <div style={{
                    background: '#2c3e50',
                    color: '#fff',
                    margin: '-20px -24px 20px -24px',
                    padding: '18px 24px',
                    borderRadius: '10px 10px 0 0',
                  }}>
                    <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
                      Confirmar generación de pago
                    </h3>
                    <div style={{ fontSize: '12px', opacity: 0.92, marginTop: '4px' }}>
                      Folio: <strong>#{targetRequest?.id}</strong> — Cliente: <strong>{targetRequest?.client_name || ''}</strong>
                    </div>
                  </div>

                  <div style={{
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '10px',
                    padding: '14px 16px',
                    marginBottom: '18px',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    color: '#1e3a8a',
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: '6px' }}>ℹ️ Texto de ayuda</div>
                    Al confirmar, el estado de la solicitud pasará a <strong>"Pagado"</strong> y se enviará un correo electrónico al cliente
                    (<strong>{targetRequest?.client_email || ''}</strong>) donde se <u>resaltará visualmente</u>:
                    <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '18px' }}>
                      <li>La <strong>fecha programada</strong> de su devolución.</li>
                      <li>El <strong>monto</strong> total a devolver.</li>
                      <li>Debajo, el texto libre que escribas en <strong>"Más información"</strong>.</li>
                    </ul>
                  </div>

                  {confirmPaymentError && (
                    <div className="error-message" style={{ marginBottom: '14px' }}>
                      {confirmPaymentError}
                    </div>
                  )}

                  <div className="modal-form">
                    <div className="modal-row">
                      <label style={{ fontWeight: 600, marginBottom: '6px' }}>
                        Fecha de pago programada <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="date"
                        name="fecha_prog_pago"
                        value={paymentConfirmForm.fecha_prog_pago}
                        onChange={handlePaymentConfirmInputChange}
                        className="form-control"
                        disabled={confirmingPayment}
                      />
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563' }}>
                        Fecha en la que se espera el depósito se refleje en la cuenta del cliente.
                      </div>
                    </div>

                    <div className="modal-row">
                      <label style={{ fontWeight: 600, marginBottom: '6px' }}>
                        Agregar detalles <span style={{ color: '#6b7280', fontWeight: 400 }}>(opcional)</span>
                      </label>
                      <textarea
                        name="payment_details"
                        value={paymentConfirmForm.payment_details}
                        onChange={handlePaymentConfirmInputChange}
                        placeholder="Ej. El depósito se realizará por transferencia SPEI. Considere un día adicional por el fin de semana..."
                        rows={4}
                        className="form-control"
                        disabled={confirmingPayment}
                        style={{ resize: 'vertical', minHeight: '88px' }}
                      />
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563' }}>
                        Este texto aparecerá en el correo del cliente bajo la sección <strong>"Más información"</strong>.
                      </div>
                    </div>
                  </div>

                  <div style={{
                    marginTop: '16px',
                    padding: '12px 14px',
                    background: '#fafafa',
                    border: '1px dashed #d1d5db',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#374151',
                  }}>
                    <div style={{ fontWeight: 700, marginBottom: '4px' }}>Resumen del correo:</div>
                    <div>Monto: <strong>${Number(targetRequest?.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong></div>
                    <div>Fecha programada: <strong>{paymentConfirmForm.fecha_prog_pago ? new Date(paymentConfirmForm.fecha_prog_pago + 'T00:00:00').toLocaleDateString('es-MX') : '(sin capturar)'}</strong></div>
                  </div>

                  <div className="modal-buttons" style={{ marginTop: '20px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowPaymentConfirmModal(false);
                        setPaymentConfirmRequestId(null);
                      }}
                      className="btn btn-secondary"
                      disabled={confirmingPayment}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmPaymented}
                      className="btn btn-warning"
                      disabled={confirmingPayment || !paymentConfirmForm.fecha_prog_pago.trim()}
                      style={{
                        background: '#d97706',
                        color: '#ffffff',
                        borderColor: '#d97706',
                        fontWeight: 600,
                      }}
                    >
                      {confirmingPayment ? 'Procesando...' : 'Confirmar y enviar correo'}
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
